import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getChart } from "@/lib/api";

const RANGES = ["1H", "24H", "7D", "ALL"];

function SyntheticChart({ coinId }) {
  const [range, setRange] = useState("24H");
  const { data } = useQuery({ queryKey: ["chart", coinId, range], queryFn: () => getChart(coinId, range) });
  const series = (data?.series || []).map((p) => ({ t: new Date(p.t).getTime(), price: p.price }));
  const last = series[series.length - 1]?.price || 0;
  const up = last >= (series[0]?.price || 0);
  const color = up ? "#00e699" : "#ff3b69";
  return (
    <div className="rounded-2xl border border-white/10 bg-[#131824]/70 p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Price (USD)</div>
          <div className="font-mono2 text-2xl font-bold text-slate-50">${last ? last.toPrecision(4) : "0.00"}</div>
        </div>
        <div className="flex gap-1 rounded-lg border border-white/10 bg-[#0d111a] p-1">
          {RANGES.map((r) => (
            <button key={r} data-testid={`chart-range-${r}`} onClick={() => setRange(r)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${range === r ? "bg-blue-500 text-white" : "text-slate-400 hover:text-white"}`}>{r}</button>
          ))}
        </div>
      </div>
      <div className="h-64 sm:h-80" data-testid="price-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="pchart" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="t" hide />
            <YAxis domain={["dataMin", "dataMax"]} hide />
            <Tooltip contentStyle={{ background: "#0d111a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }}
              labelFormatter={(t) => new Date(t).toLocaleString()} formatter={(v) => ["$" + Number(v).toPrecision(4), "Price"]} />
            <Area type="monotone" dataKey="price" stroke={color} strokeWidth={2} fill="url(#pchart)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function PriceChart({ coinId, tokenAddress }) {
  // Live DexScreener chart for on-chain tokens; synthetic fallback for samples.
  if (tokenAddress) {
    return (
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#131824]/70" data-testid="dexscreener-chart">
        <div className="flex items-center justify-between border-b border-white/5 px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Live chart · DexScreener</span>
          <a href={`https://dexscreener.com/base/${tokenAddress}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#38bdf8] hover:underline">Open ↗</a>
        </div>
        <div style={{ position: "relative", width: "100%", paddingBottom: "62%" }}>
          <iframe
            title="DexScreener"
            src={`https://dexscreener.com/base/${tokenAddress}?embed=1&theme=dark&trades=0&info=0`}
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
          />
        </div>
        <p className="px-4 py-2 text-[11px] text-slate-500">Freshly launched tokens may take a few minutes to index on DexScreener.</p>
      </div>
    );
  }
  return <SyntheticChart coinId={coinId} />;
}
